import React, { useEffect, useRef, useState } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { VideoPlayer } from '@thd-olt-component-react/video-player';
import { Image as MainAssetImage } from '@thd-olt-component-react/image';
import { PaintSwatches } from '@one-thd/sui-icons';
import { ARIcon, BackgroundCircleIcon, HotspotTagIcon, ThreeSixtyIcon } from '../../../Icons';
import { ReactImageMagnify } from '../../../ReactImageMagnify/ReactImageMagnify';
import Helper from '../../../../Helper';
import Threesixty from '../../../Threesixty';
import { AvataarIFrame } from '../AvataarIFrame/AvataarIFrame';

const {
  elementType, shape, string, bool, number
} = proptypes;

// 650px cap, dropping to 500px at or below the $screen-lg (1440px) width or a 768px viewport
// height. max-2xl would exclude 1440 itself, so the inclusive query is spelled out.
const STANDARD_IMAGE_CLASSES = 'sui-w-full sui-h-full sui-m-auto sui-max-h-[650px] sui-max-w-[650px] '
  + '[@media(max-width:1440px)]:sui-max-h-[500px] [@media(max-width:1440px)]:sui-max-w-[500px] '
  + '[@media(max-height:768px)]:sui-max-h-[500px] [@media(max-height:768px)]:sui-max-w-[500px]';

const STANDARD_IMAGE_NO_CURSOR_CLASSES = `${STANDARD_IMAGE_CLASSES} sui-cursor-default`;

const NORMAL_IMAGE_WRAPPER_CLASSES = 'sui-relative sui-overflow-visible sui-mx-auto sui-h-full '
  + '[&_iframe]:sui-w-full [&_iframe]:sui-h-full';

const IMAGE_FIT_BRAND_ABSOLUTE_CLASSES = 'sui-absolute sui-left-0 sui-bottom-0 sui-flex '
  + 'sui-items-end sui-aspect-square';

const SHOP_THIS_ROOM_CONTAINER_CLASSES = 'sui-w-full sui-h-full sui-mx-auto sui-text-right '
  + 'sui-text-sm sui-relative sui-bottom-8 sui-right-2 sui-max-h-[750px] sui-max-w-[750px]';

// #000000 pill behind the link; sui-bg-inverse is #202020, a visibly different swatch.
const SHOP_THIS_ROOM_LINK_CLASSES = 'sui-text-inverse sui-bg-[#000000] sui-opacity-70 '
  + 'sui-py-1 sui-px-1';

// flex:2 against the flex:1 side content of the overlay.
const MAIN_ASSET_CLASSES = 'sui-relative sui-overflow-visible sui-m-2 sui-h-full sui-flex-[2]';

const CTA_CLASSES = 'sui-flex sui-justify-center';

export const MainAsset = (
  {
    fwdOverlayRef = null,
    mainAsset = {
      id: '',
      url: '',
      mediaType: ''
    },
    zoomedAsset = {
      id: '',
      url: '',
      mediaType: ''
    },
    videoAnalyticsData,
    itemId = '',
    showAvataarAR = false,
    showAvataarDefault = false,
    selectedThumbnailIndex = 0
  }
) => {
  const imageRef = useRef(null);
  const [imageSize, setImageSize] = useState(1000);
  const avataarExperienceLink = Helper.getAvataarExperienceLink(itemId, showAvataarAR);

  useEffect(() => {
    if (imageRef?.current) {
      imageRef.current.style.left = '0px';
      imageRef.current.style.right = '0px';
    }
  }, [mainAsset]);

  useEffect(() => {
    if (!zoomedAsset?.url) {
      return undefined;
    }

    const zoomedImage = new Image();
    zoomedImage.onload = () => {
      const imageNaturalSize = zoomedImage.naturalWidth;
      const normalizedWidth = imageNaturalSize < 1000 ? 1000 : imageNaturalSize;
      setImageSize(normalizedWidth);
    };
    zoomedImage.src = zoomedAsset.url;

    return () => {
      zoomedImage.onload = null;
    };
  }, [zoomedAsset?.url]);

  if (!mainAsset) {
    return null;
  }

  const isAR = Helper.isAR({ media: mainAsset });
  const isAvataarAR = Helper.isAvataarAR({ media: mainAsset });
  const isImage = Helper.isImage({ media: mainAsset });
  const isImage360 = Helper.isImage360({ media: mainAsset });
  const isVideo = Helper.isVideo({ media: mainAsset });
  const isHotspot = Helper.isHotspot({ media: mainAsset });
  const isCustomColor = Helper.isCustomColor({ media: mainAsset });
  const isManualColor = Helper.isManualColor({ media: mainAsset });
  const isImageOnly = !isHotspot && isImage;

  const { source, videoSource, videoId } = Helper.formatVideoProps(mainAsset);

  const enlargedImageContainerDimensions = {
    width: '100%',
    height: '100%'
  };

  const magnifyLargeImage = {
    src: zoomedAsset?.url || mainAsset?.brandLogo,
    width: imageSize,
    height: imageSize
  };

  const imageMagnifyProps = {
    smallImage: {
      isFluidWidth: true,
      src: zoomedAsset?.url || mainAsset?.brandLogo
    },
    largeImage: magnifyLargeImage,
    lensStyle: {
      backgroundImage: 'url(https://assets.thdstatic.com/images/v1/zoom-screen-4X4.svg)',
    },
    enlargedImagePosition: 'over',
    enlargedImageContainerDimensions,
    isActivatedOnTouch: true
  };

  return (
    <div data-testid="main-asset" className={MAIN_ASSET_CLASSES}>
      <div data-testid="main-asset-normal-image-wrapper" className={NORMAL_IMAGE_WRAPPER_CLASSES}>
        {isImage360 && (
          <Threesixty
            media={mainAsset}
            fwdOverlayRef={fwdOverlayRef}
            fwdImageRef={imageRef}
          />
        )}
        {isImageOnly && (
          <div>
            <div className={STANDARD_IMAGE_CLASSES}>
              <ReactImageMagnify {...imageMagnifyProps} />
            </div>
            {mainAsset.collection
              && (
                <div className={SHOP_THIS_ROOM_CONTAINER_CLASSES}>
                  <a
                    data-testid="shop-this-room-link"
                    className={SHOP_THIS_ROOM_LINK_CLASSES}
                    target="_blank"
                    rel="noreferrer"
                    href={mainAsset.collection}
                    onClick={(e) => e && e.stopPropagation()}
                  >
                    Shop This Room
                  </a>
                </div>
              )}
            {!Helper.isTouchEnabled()
              && <span className={CTA_CLASSES}>Hover Image to Zoom</span>}
            {Helper.isTouchEnabled()
              && <span className={CTA_CLASSES}>Tap and Hold to Zoom</span>}
          </div>
        )}
        {isAR
          && isAvataarAR
          && (showAvataarAR || showAvataarDefault)
          && avataarExperienceLink
          && <AvataarIFrame key="main-asset" avataarExperienceLink={avataarExperienceLink} />}
        {isAR && isAvataarAR && !showAvataarAR && !showAvataarDefault && (
          <div className="sui-text-center">
            <MainAssetImage
              className={STANDARD_IMAGE_NO_CURSOR_CLASSES}
              src={mainAsset.mediaStill}
              alt=""
              lazy
            />
            <BackgroundCircleIcon />
            <ThreeSixtyIcon />
          </div>
        )}
        {isAR && !isAvataarAR && !showAvataarAR && !showAvataarDefault && (
          <div className="sui-text-center">
            <MainAssetImage
              className={STANDARD_IMAGE_NO_CURSOR_CLASSES}
              src={mainAsset.mediaStill}
              alt=""
              lazy
            />
            <BackgroundCircleIcon />
            <ARIcon />
          </div>
        )}
        {isHotspot && (
          <div className={STANDARD_IMAGE_CLASSES}>
            <HotspotTagIcon
              mediaGalleryIndex={selectedThumbnailIndex}
              openOverlay={false}
              hotspots={mainAsset?.hotspots}
              ocrHotspots={mainAsset?.ocrHotspots}
              imageUrl={zoomedAsset?.url}
            />
          </div>
        )}
        {isVideo && (
          // sui-contents keeps this wrapper out of layout; VideoPlayer only forwards className.
          <div className="sui-contents" data-testid="main-asset-video-player">
            <VideoPlayer
              videoId={videoId}
              source={source}
              videoSrc={videoSource}
              analyticsData={{ ...videoAnalyticsData, component: 'thumbnails' }}
            />
          </div>
        )}
        {isManualColor && (
          <div
            className={classNames('sui-bg-inactive',
              'sui-flex',
              'sui-items-center',
              'sui-justify-center',
              'sui-w-full',
              'sui-h-full'
            )}
          >
            <div className="sui-text-center sui-flex sui-items-center sui-justify-center">
              <div>
                <PaintSwatches color="medium" />
                <div>No Color Preview Available</div>
              </div>
              <div
                className={IMAGE_FIT_BRAND_ABSOLUTE_CLASSES}
              >
                <MainAssetImage
                  className="sui-w-64"
                  alt={mainAsset.alt}
                  src={mainAsset.brandLogo}
                />
              </div>
            </div>
          </div>
        )}
        {isCustomColor && (
          <div
            className={STANDARD_IMAGE_CLASSES}
          >
            <div
              className="sui-flex sui-items-end sui-aspect-square"
              style={{
                backgroundColor: `rgb(${mainAsset.rgb.red},${mainAsset.rgb.green},${mainAsset.rgb.blue})`
              }}
            >
              <MainAssetImage
                className="sui-w-64"
                alt={mainAsset.alt}
                src={mainAsset.brandLogo}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MainAsset.propTypes = {
  mainAsset: shape({
    id: string,
    url: string,
    mediaType: string
  }),
  zoomedAsset: shape({
    id: string,
    url: string,
    mediaType: string
  }),
  fwdOverlayRef: shape({ current: elementType }),
  videoAnalyticsData: shape({
    section: string.isRequired,
    position: string.isRequired
  }).isRequired,
  itemId: string,
  showAvataarAR: bool,
  showAvataarDefault: bool,
  selectedThumbnailIndex: number
};

export default MainAsset;
